import type { ErrorEvent } from "@sentry/react";

const FILTERED = "[Filtered]";

const SENSITIVE_KEY_PATTERN =
  /authorization|cookie|password|secret|credential|api[-_]?key|(?:^|[^a-z])token/i;

const SENSITIVE_VALUE_PATTERNS: [RegExp, string][] = [
  [/\bBearer\s+[\w.\-+/=:]+/gi, FILTERED],
  [/\bjwt:[\w.\-+/=]+/gi, FILTERED],
  [/\beyJ[\w-]+\.[\w-]+\.[\w-]+/g, FILTERED],
  // query-string secrets, e.g. ?access_token=… — the capture keeps the key name
  [
    /([?&][^=&#]*(?:token|key|secret|password|auth|credential)[^=&#]*=)[^&#]*/gi,
    `$1${FILTERED}`,
  ],
];

const MAX_DEPTH = 8;

export function scrubSensitiveData(event: ErrorEvent): ErrorEvent {
  if (event.request?.url) {
    event.request.url = scrubString(event.request.url);
  }
  scrubInPlace(event.request?.headers, 0);
  scrubInPlace(event.request?.cookies, 0);
  scrubInPlace(event.request?.data, 0);
  scrubInPlace(event.contexts, 0);
  scrubInPlace(event.extra, 0);

  event.exception?.values?.forEach((exception) => {
    if (typeof exception.value === "string") {
      exception.value = scrubString(exception.value);
    }
  });

  event.breadcrumbs?.forEach((breadcrumb) => {
    scrubInPlace(breadcrumb.data, 0);
    if (typeof breadcrumb.message === "string") {
      breadcrumb.message = scrubString(breadcrumb.message);
    }
  });

  return event;
}

function scrubInPlace(container: unknown, depth: number): void {
  if (
    depth >= MAX_DEPTH ||
    typeof container !== "object" ||
    container === null
  ) {
    return;
  }
  Object.entries(container).forEach(([key, value]) => {
    if (!Array.isArray(container) && SENSITIVE_KEY_PATTERN.test(key)) {
      Object.assign(container, { [key]: FILTERED });
      return;
    }
    if (typeof value === "string") {
      Object.assign(container, { [key]: scrubString(value) });
      return;
    }
    scrubInPlace(value, depth + 1);
  });
}

export function scrubSensitiveString(value: string): string {
  return scrubString(value);
}

function scrubString(value: string): string {
  return SENSITIVE_VALUE_PATTERNS.reduce(
    (scrubbed, [pattern, replacement]) =>
      scrubbed.replace(pattern, replacement),
    value,
  );
}
